/**
 * Performance monitoring utilities
 */

// Web Vitals tracking
export const trackWebVitals = () => {
    if (typeof window === 'undefined') return;

    // Track Largest Contentful Paint (LCP)
    const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];

        if (process.env.NODE_ENV === 'development') {
            console.log('LCP:', lastEntry.startTime);
        }
    });

    try {
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
        // Fallback for browsers that don't support LCP
        console.warn('LCP monitoring not supported');
    }

    // Track First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('FID:', entry.processingStart - entry.startTime);
            }
        });
    });

    try {
        fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
        console.warn('FID monitoring not supported');
    }
};

// Resource loading optimization
export const optimizeResourceLoading = () => {
    // Preload critical resources
    const preloadResource = (href, as, type = null) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = href;
        link.as = as;
        if (type) link.type = type;
        document.head.appendChild(link);
    };

    // Preload critical fonts
    preloadResource(
        'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
        'style'
    );
};

// Image optimization
export const createImageOptimizer = () => {
    const imageCache = new Map();

    return {
        getOptimizedSrc: (src, width = 800, quality = 80) => {
            if (!src) return null;

            const cacheKey = `${src}-${width}-${quality}`;
            if (imageCache.has(cacheKey)) {
                return imageCache.get(cacheKey);
            }

            let optimizedSrc = src;

            // Add optimization for different image services
            if (src.includes('unsplash.com')) {
                optimizedSrc = `${src}?w=${width}&q=${quality}&auto=format`;
            } else if (src.includes('cloudinary.com')) {
                optimizedSrc = src.replace('/upload/', `/upload/w_${width},q_${quality},f_auto/`);
            }

            imageCache.set(cacheKey, optimizedSrc);
            return optimizedSrc;
        },

        preloadImage: (src) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = src;
            });
        }
    };
};

// Bundle size monitoring
export const monitorBundleSize = () => {
    if (process.env.NODE_ENV === 'development' && 'performance' in window) {
        window.addEventListener('load', () => {
            const navigation = performance.getEntriesByType('navigation')[0];
            console.log('Bundle load time:', navigation.loadEventEnd - navigation.loadEventStart, 'ms');
            console.log('DOM content loaded:', navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart, 'ms');
        });
    }
};